// ⚠️  MOBILE-FIRST. Full-screen first-run welcome — 3 slides ending in a guided
// first workout (Squats). Shown once per account (`user.onboarded` flag in DB).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Lock, Flame, ChevronRight, Play } from "lucide-react";
import { listExercises, updateProfile } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const SLIDES = [
    {
        icon: Camera,
        title: "Your camera counts your reps",
        body: "Prop your phone against a wall, get in frame, and move. AI pose tracking counts every rep and times every hold — no equipment, no manual logging.",
    },
    {
        icon: Lock,
        title: "Private by design",
        body: "All movement analysis happens on your device, right in the browser. Your video is never recorded, never uploaded, never leaves your phone.",
    },
    {
        icon: Flame,
        title: "Earn coins. Keep the streak.",
        body: "Every rep earns coins. Hit a small daily goal — one set, about 90 seconds — to grow your streak and climb the leaderboard.",
    },
];

export default function Onboarding() {
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [squatsId, setSquatsId] = useState(null);
    const { refresh } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        listExercises()
            .then((r) => {
                const squats = (r.data || []).find((e) => e.slug === "squats");
                if (squats) setSquatsId(squats.exercise_id);
            })
            .catch((err) => console.warn("[onboarding] exercises fetch failed:", err?.message || err));
    }, []);

    const complete = async (startSquats) => {
        setBusy(true);
        try {
            await updateProfile({ onboarded: true });
        } catch (err) {
            console.warn("[onboarding] failed to persist flag:", err?.message || err);
        }
        if (startSquats) {
            navigate(squatsId ? `/workout/${squatsId}` : "/exercises");
        }
        await refresh();
        setBusy(false);
    };

    const slide = SLIDES[step];
    const last = step === SLIDES.length - 1;
    const Icon = slide.icon;

    return (
        <div className="onb" data-testid="onboarding-overlay">
            <button
                className="onb-skip"
                onClick={() => complete(false)}
                disabled={busy}
                data-testid="onboarding-skip"
            >
                Skip
            </button>

            <div className="onb-body" key={step}>
                <div className="onb-art"><Icon size={44} /></div>
                <h1 className="onb-h">{slide.title}</h1>
                <p className="onb-p">{slide.body}</p>
            </div>

            <div className="onb-foot">
                <div className="onb-dots">
                    {SLIDES.map((_, i) => (
                        <span key={i} className={i === step ? "on" : ""} />
                    ))}
                </div>
                {!last ? (
                    <button className="btn-block" onClick={() => setStep(step + 1)} data-testid="onboarding-next">
                        Next <ChevronRight size={18} />
                    </button>
                ) : (
                    <>
                        <button
                            className="btn-block"
                            onClick={() => complete(true)}
                            disabled={busy}
                            data-testid="onboarding-start-squats"
                        >
                            <Play size={18} /> {busy ? "Starting…" : "Try it now — Squats"}
                        </button>
                        <button
                            className="onb-ghost"
                            onClick={() => complete(false)}
                            disabled={busy}
                            data-testid="onboarding-explore"
                        >
                            I'll explore on my own
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
